import { useState, useRef, useCallback } from 'react';
import { usePlanningStore, type ViewFilter } from '@/stores/planning';
import { useAuthStore } from '@/stores/auth';
import { planningEmployeesOnly } from '@/utils/planningScope';
import { usePermissionsStore } from '@/stores/permissions';
import { useClickOutside } from '@/hooks/useClickOutside';

const VIEW_FILTERS: { value: ViewFilter; label: string }[] = [
  { value: 'installers', label: 'Installateurs' },
  { value: 'pl_inst', label: 'PL + Inst.' },
  { value: 'per_day', label: 'Per dag' },
];

export function FilterDropdown() {
  const viewFilter = usePlanningStore((s) => s.viewFilter);
  const setViewFilter = usePlanningStore((s) => s.setViewFilter);
  const showAllInstallers = usePlanningStore((s) => s.showAllInstallers);
  const setShowAllInstallers = usePlanningStore((s) => s.setShowAllInstallers);
  const multiWeek = usePlanningStore((s) => s.multiWeek);
  const setMultiWeek = usePlanningStore((s) => s.setMultiWeek);
  const hideCompleted = usePlanningStore((s) => s.hideCompleted);
  const toggleHideCompleted = usePlanningStore((s) => s.toggleHideCompleted);
  const resetInstallerRowOrder = usePlanningStore((s) => s.resetInstallerRowOrder);

  // Administratie on its own gets no Weergave; with a full-planning role it gets that role's (utils/planningScope.ts).
  const employeesOnly = useAuthStore((s) => planningEmployeesOnly(s.hasRole));
  // Warehouse is forced to the flat installer rows (PlanningView), so "Per dag"
  // would do nothing for it.
  const isWarehouse = useAuthStore((s) => s.hasRole('warehouse'));
  const viewFilters = isWarehouse ? VIEW_FILTERS.filter((f) => f.value !== 'per_day') : VIEW_FILTERS;
  const canPlanningEdit = usePermissionsStore((s) => s.canFeature('planning_edit'));
  const effectiveViewFilter = employeesOnly ? 'installers' : viewFilter;

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useClickOutside(containerRef, close, open);

  const hasActiveFilters = employeesOnly
    ? hideCompleted
    : viewFilter !== 'installers' || showAllInstallers || hideCompleted;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`relative p-1.5 rounded-lg border transition-colors ${
          open ? 'bg-gray-100 border-gray-400' : 'border-gray-300 hover:bg-gray-50'
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Filters"
      >
        <svg
          className="w-4 h-4 text-td-navy"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        {hasActiveFilters && (
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-td-blue rounded-full" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50 py-2" role="dialog" aria-label="Filters">
          {!employeesOnly && (
            <>
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-gray-500 mb-2">Weergave</p>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  {viewFilters.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setViewFilter(value)}
                      className={`flex-1 px-3 py-1.5 text-xs font-semibold transition-colors ${
                        viewFilter === value
                          ? 'bg-td-blue text-white'
                          : 'text-td-navy hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-t border-gray-100" />
              {/* "Per dag" has no installer rows to show or hide. */}
              {viewFilter !== 'per_day' && (
                <>
                  <label className="flex items-center gap-2 px-4 py-2.5 text-xs text-td-navy cursor-pointer select-none hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={showAllInstallers}
                      onChange={(e) => setShowAllInstallers(e.target.checked)}
                      className="rounded border-gray-300 text-td-blue focus:ring-td-blue h-3.5 w-3.5"
                    />
                    Alle installateurs
                  </label>
                  <div className="border-t border-gray-100" />
                </>
              )}
            </>
          )}

          <label className="flex items-center gap-2 px-4 py-2.5 text-xs text-td-navy cursor-pointer select-none hover:bg-gray-50">
            <input
              type="checkbox"
              checked={multiWeek}
              onChange={(e) => setMultiWeek(e.target.checked)}
              className="rounded border-gray-300 text-td-blue focus:ring-td-blue h-3.5 w-3.5"
            />
            Scroll door weken
          </label>
          <div className="border-t border-gray-100" />

          <label className="flex items-center gap-2 px-4 py-2.5 text-xs text-td-navy cursor-pointer select-none hover:bg-gray-50">
            <input
              type="checkbox"
              checked={hideCompleted}
              onChange={() => toggleHideCompleted()}
              className="rounded border-gray-300 text-td-blue focus:ring-td-blue h-3.5 w-3.5"
            />
            Verberg afgerond
          </label>

          {/* Reset writes preferences (planning:write) — editor-only, so read-only
              viewers can't trigger a 403 "Volgorde resetten mislukt" toast. */}
          {effectiveViewFilter === 'installers' && canPlanningEdit && (
            <>
              <div className="border-t border-gray-100" />
              <div className="px-4 py-2">
                <button
                  type="button"
                  onClick={() => void resetInstallerRowOrder()}
                  className="w-full px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-td-navy hover:bg-gray-50 transition-colors"
                >
                  Reset volgorde
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
